import { useState } from "react";
import { TextFieldProps } from "@mui/material";

import { DatePicker, DatePickerSlotProps } from "@mui/x-date-pickers";
import { isNaN } from "formik";

export interface NewDatePickerProps {
  disabled?: boolean;
  required?: boolean;
  /**
   * Date object created by `backend.parseDate` or `new Date` if using a full datetime string
   */
  date: Date | undefined;
  setDate: (date: Date | undefined) => void;
  error?: string | undefined;
  setError?: (error: string | undefined) => void;
  minDate?: Date;
  maxDate?: Date;
  label?: string;
  format?: string;
  inputVariant?: TextFieldProps["variant"];
  helperText?: string;
  slotProps?: DatePickerSlotProps<Date, boolean> | undefined;
}
const PDatePicker = ({
  disabled,
  required,
  date,
  setDate,
  minDate,
  maxDate,
  label,
  format = "MM/dd/yyyy",
  inputVariant,
  error,
  setError,
  helperText,
  slotProps,
}: NewDatePickerProps) => {
  const [isOpen, setOpen] = useState(false);

  const handleError = (error?: string) => {
    if (setError) {
      switch (error) {
        case "minDate":
          setError("Date is before minimum date");
          break;
        case "maxDate":
          setError("Date is after maximum date");
          break;
        case undefined:
          setError(undefined);
          break;
        default:
          setError("Invalid date");
      }
    }
  };

  // Errors take precedence over other usages of the helperText
  const coalescedErrorText = error || helperText;
  const helperTextNode = coalescedErrorText ? (
    <span style={{ fontSize: "10px" }}>{coalescedErrorText}</span>
  ) : undefined;

  return (
    <DatePicker
      data-pendo="date-picker"
      disabled={disabled}
      label={label}
      format={format}
      minDate={minDate}
      maxDate={maxDate}
      open={isOpen}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={date ? date : null}
      desktopModeMediaQuery="@media (min-width: 0px)"
      onChange={(date: Date | null, error) => {
        setDate(date || undefined);
        if (error.validationError) {
          handleError(error.validationError);
        } else if (date && isNaN(date.getTime())) {
          handleError("Invalid Date");
        } else {
          handleError(undefined);
        }
      }}
      slotProps={{
        popper: {
          placement: "bottom-end",
        },
        field: {
          clearable: true,
        },
        textField: {
          sx: {
            mt: 0,
            minWidth: 133,
          },
          required: required,
          margin: "dense",
          size: "small",
          variant: inputVariant || "outlined",
          helperText: helperTextNode,
          fullWidth: true,
        },
        openPickerButton: {
          // @ts-expect-error slot props are not updated to take arbritrary attributes
          "data-pendo": "date-picker-calendar-button",
        },
        ...slotProps,
      }}
    />
  );
};

export default PDatePicker;
